import './user-cards.system-administrator.screen.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../i18n/translations';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {UserCard} from '../../../../../../backend/interfaces/user-card';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {useContext, useEffect, useState} from 'react';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import {UserCardsRepository} from '../../../../../../repositories/user-cards';
import AddIcon from '@mui/icons-material/Add';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {Box, Button, Stack} from '@mui/material';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {UserPublicInfo} from '../../../../../../backend/responses/user-public-info';
import {AuxiliaryDataCacheContext} from '../../../../../../contexts/auxiliary-data-cache/context';
import {AuxiliaryDataCacheContextType} from '../../../../../../contexts/auxiliary-data-cache/types';
import {DataCacheManager} from '../../../../../../managers/data-cache';
import {UsersRepository} from '../../../../../../repositories/users';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  State = 'state',
  User = 'user',
}

export default function UserCardsSystemAdministratorOfficeScreen() {
  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Get context
  const auxiliaryDataCacheContext: AuxiliaryDataCacheContextType =
    useContext(AuxiliaryDataCacheContext);

  // Hold state
  const [deletedUserCardName, setDeletedUserCardName] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.UserCardsDeletedQuery)
  );

  // Fetch cards
  const handleGetCards = async (limit: number, skip: number, search?: string) => {
    // Load cards
    const result: {totalCount: number; data: Array<UserCard>} =
      await UserCardsRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        undefined,
        undefined,
        false,
        search
      );

    // Process data
    const processedData: PaginatedTableData<UserCard> = result.data.map((obj) => {
      // Get the user info
      const userInfo = obj.userId ? getUserInfo(obj.userId) : undefined;

      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        toLink: `${path}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.User,
            value: userInfo?.name || undefined,
            avatarUrl: userInfo?.picture || undefined,
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.user_cards.root.state_value_true)
              : t(translations.user_cards.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<UserCard>({
    fetchData: handleGetCards,
  });

  // Fetch user info
  const fetchUserInfo = async (userId: string) => {
    let updateContext: boolean = false;

    try {
      // User is being fetched
      DataCacheManager.instance.addPendingUser(userId);

      // Fetch user info
      const user: UserPublicInfo = await UsersRepository.instance.getInfoById(userId);

      if (user) {
        // Save it on cache
        DataCacheManager.instance.addUser(userId, user);

        // Update
        updateContext = true;
      } else {
        // Error
        throw new Error('Empty user.');
      }
    } catch (error) {
      // Log
      console.debug(`🔴 Could not get user ${userId}.`, error);

      // Save it on cache to not fetch again
      DataCacheManager.instance.addUserWithError(userId);
    } finally {
      // User was fetched
      DataCacheManager.instance.removePendingUser(userId);

      // Update context with users
      if (updateContext) {
        auxiliaryDataCacheContext.setUsers(DataCacheManager.instance.users);
      }
    }
  };

  // Get user info
  const getUserInfo = (userId: string): UserPublicInfo | undefined => {
    if (DataCacheManager.instance.users[userId]) {
      // Return user info
      return DataCacheManager.instance.users[userId];
    } else {
      if (!DataCacheManager.instance.usersWithError[userId]) {
        if (!DataCacheManager.instance.pendingUsers[userId]) {
          // Fetch user info but don't wait for it
          fetchUserInfo(userId);
        } else {
          // Log
          console.debug("⌛ User already being fetched. Let's wait for id:", userId);
        }
      } else {
        // Log
        console.debug(
          '🔴 Could not get the user because an error occurred previously with id:',
          userId
        );
      }
    }
  };

  // Update the data when the users in the auxiliary cache were updated
  useEffect(() => {
    for (let i = 0; i < data.length; i++) {
      if (
        data[i].item.userId &&
        !data[i].columns.find((col) => col.columnId === ColumnIdentifier.User)?.value
      ) {
        const userInfo = getUserInfo(data[i].item.userId as string);

        if (userInfo) {
          handleUpdateItem(data[i].id, [
            {
              updatedColumnId: ColumnIdentifier.User,
              updatedColumnValue: userInfo.name,
              updatedColumnAvatarUrl: userInfo.picture,
            },
          ]);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxiliaryDataCacheContext.state]);

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedUserCardName(null);

    // Reset history
    history.replace(path);
  };

  return (
    <>
      <GenericHeader
        isLoading={false}
        pageTitle={t(translations.office.professional_caregiver.tabs.user_cards)}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.user_cards.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={
          <Button
            endIcon={<AddIcon />}
            onClick={() => {
              history.push(`${path}/create`);
            }}
            variant="inverted"
          >
            {t(translations.user_cards.root.add_button)}
          </Button>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.user_cards.root.no_cards)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.user_cards.root.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.User,
                title: t(translations.user_cards.root.user_header),
                flexGrow: 1,
                chip: true,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.user_cards.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      <AlertModal
        id="user-card-deleted-warning"
        opened={deletedUserCardName ? true : false}
        title={t(translations.user_cards.root.deleted_card_dialog_title, {
          name: deletedUserCardName,
        })}
        content={t(translations.user_cards.root.deleted_card_dialog_message)}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(translations.user_cards.root.deleted_card_dialog_ok_button),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </>
  );
}
